import { Button, Checkbox, Dropdown, Icon, Input, PasswordInput, Textarea, ToggleHeight } from '@column/column-ui-kit';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  Option,
  OptionList,
  OtherContainerSmall,
  RegisterAction,
  RegisterContent,
  RegisterContentGrid,
  StyledTextarea,
} from '../Questions';
import { Form } from '../../Login';
import { FormElementHint } from './Location';
import { ROUTE } from '~/public/routes';
import { FormElement, FormFields, FormLabel, Headline, Line, Paragraph } from '~/styles';
import {
  BusinessPageIdType,
  documentsDropdownOptions,
  DocumentsType,
  DocumentsValueType,
  PlatformRootEntityCache,
} from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { FileUpload, NotificationList } from '~/components';
import { useNotificationStore } from '~/stores/Notification';
import { validate4Digits, validateURL } from '~/util';

export const FormElementSplit = styled.div`
  margin-top: 4px;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
`;

export const ButtonLeft = styled(Button)`
  justify-self: start;
  margin-top: 4px;
`;

export const PageRegisterBusinessInformation: React.FC = () => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const currentPlatformState = useSessionStore((state) => state.currentPlatform);
  const rootEntity = useSessionStore((state) => state.rootEntity);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDBA, setIsDBA] = useState<boolean>(false);
  const [sourceOfFunds, setSourceOfFunds] = useState<string[]>([]);
  const [sourceOfFundsOther, setSourceOfFundsOther] = useState<string>('');
  const [sourceOfFundsOtherError, setSourceOfFundsOtherError] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { control, handleSubmit, watch, reset, setValue } = useForm<PlatformRootEntityCache>();

  const handleSourceOfFundsChange = (optionKey: string) => {
    if (sourceOfFunds.includes(optionKey)) {
      setSourceOfFunds(sourceOfFunds.filter((k) => k !== optionKey));
      return;
    }
    setSourceOfFunds([...sourceOfFunds, optionKey]);
  };

  const onSuccess = (rootEntityCache: PlatformRootEntityCache) => {
    if (isLoading) {
      return;
    }

    setSourceOfFundsOtherError(false);

    if (sourceOfFunds.length < 1) {
      addDangerNotification({
        content: 'Please select at least one option.',
        display: 'source-of-funds',
      });
      return;
    }

    if (sourceOfFunds.includes('Other') && sourceOfFundsOther.length < 1) {
      addDangerNotification({
        content: 'Please specify source of funds.',
        display: 'source-of-funds',
      });

      setSourceOfFundsOtherError(true);

      return;
    }

    setIsLoading(true);

    currentPlatformState
      ?.updateRootEntityCache({
        ...rootEntityCache,
        sourceOfFunds,
        sourceOfFundsOther,
        completedPages: [
          ...new Set<BusinessPageIdType>([
            ...(currentPlatformState.rootEntityCache?.completedPages ?? []),
            'information',
          ]),
        ],
      })
      .then(() => {
        navigate(ROUTE.REGISTER_BUSINESS_LOCATION);
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'page',
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleFileUpload = useCallback(
    (file: DocumentsType, einOrTaxIdDocumentType?: DocumentsValueType) => {
      setValue('einOrTaxIdDocumentId', file.id);
      setValue('einOrTaxIdDocumentType', einOrTaxIdDocumentType);

      currentPlatformState
        ?.updateRootEntityCache({
          einOrTaxIdDocumentId: file.id,
          einOrTaxIdDocumentType,
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
            display: 'file-upload',
          });
        });
    },
    [currentPlatformState]
  );

  const handleFileRemove = useCallback(
    (fileId: string) => {
      if (rootEntity) {
        return;
      }

      Promise.all([
        currentPlatformState?.updateRootEntityCache({
          einOrTaxIdDocumentId: undefined,
          einOrTaxIdDocumentType: undefined,
        }),
        currentPlatformState?.updateMeta({
          docSubmittedPreRootEntityCreation:
            currentPlatformState.docSubmittedPreRootEntityCreation?.filter((id) => id !== fileId) ?? [],
        }),
      ])
        .then(() => {
          setValue('einOrTaxIdDocumentId', undefined);
          setValue('einOrTaxIdDocumentType', undefined);
        })
        .catch((error) => {
          setTimeout(() => {
            addDangerNotification({
              content: error.message,
              display: 'file-upload',
            });
          }, 400);
        });
    },
    [rootEntity, currentPlatformState]
  );

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({ currentPlatform: state.currentPlatform, isLoadingState: state.isLoading }),
        ({ currentPlatform, isLoadingState }) => {
          if (isLoadingState) {
            return;
          }

          reset({
            ...currentPlatform?.rootEntityCache,
            businessName:
              currentPlatform?.rootEntityCache?.businessName &&
              currentPlatform?.rootEntityCache?.businessName.length > 0
                ? currentPlatform?.rootEntityCache?.businessName
                : currentPlatform?.companyName ?? '',
          });

          setSourceOfFunds(currentPlatform?.rootEntityCache?.sourceOfFunds ?? []);
          setSourceOfFundsOther(currentPlatform?.rootEntityCache?.sourceOfFundsOther ?? '');

          if (currentPlatform?.rootEntityCache?.dbaName) {
            setIsDBA(true);
          }
        },
        { fireImmediately: true }
      ),
    [location.pathname]
  );

  return (
    <RegisterContent>
      <RegisterContentGrid>
        <Headline>Business information</Headline>
        <Paragraph size="small">
          As a bank, we are required to collect and verify information about your business, in a process called KYC.
        </Paragraph>
      </RegisterContentGrid>
      <Form onSubmit={handleSubmit(onSuccess)}>
        <NotificationList display="page" />
        <RegisterContent>
          <FormFields>
            <FormElement>
              <FormElement>
                <FormLabel>Business Legal Name</FormLabel>
                <FormElementHint>This is the full name you use on legal documents, tax forms, etc.</FormElementHint>
                <Controller
                  name="businessName"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState: { error, isTouched } }) => (
                    <Input placeholder="Business Name" hasError={isTouched && !!error} {...field} />
                  )}
                />
              </FormElement>

              {!isDBA && (
                <ButtonLeft variant="subtle" type="button" icon={<Icon.Plus />} onClick={() => setIsDBA(true)}>
                  Add DBA (Doing Business As) Name
                </ButtonLeft>
              )}

              {isDBA && (
                <FormElementSplit>
                  <Controller
                    name="dbaName"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: isDBA,
                    }}
                    render={({ field, fieldState: { error, isTouched } }) => (
                      <Input placeholder="DBA (Doing Business As) name" hasError={isTouched && !!error} {...field} />
                    )}
                  />
                  <Button
                    variant="muted"
                    type="button"
                    icon={<Icon.Cross />}
                    onlyIcon
                    onClick={() => setIsDBA(false)}
                  />
                </FormElementSplit>
              )}
            </FormElement>

            <FormElement>
              <FormLabel>Website</FormLabel>
              <Controller
                name="website"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                  validate: validateURL,
                }}
                render={({ field, fieldState: { error, isTouched } }) => (
                  <Input placeholder="website.com" hasError={isTouched && !!error} {...field} />
                )}
              />
            </FormElement>

            <div>
              <FormElement>
                <FormLabel>Legal Entity Type</FormLabel>
                <Controller
                  name="legalEntityType"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Dropdown
                      onChange={field.onChange}
                      active={field.value}
                      hasError={!!error}
                      options={[
                        {
                          label: 'Corporation',
                          value: 'corporation',
                        },
                        {
                          label: 'Limited partnership',
                          value: 'limited-partnership',
                        },
                        {
                          label: 'General partnership',
                          value: 'general-partnership',
                        },
                        {
                          label: 'LLC',
                          value: 'llc',
                        },
                        {
                          label: 'Sole proprietorship',
                          value: 'sole-proprietorship',
                        },
                        {
                          label: 'Trust',
                          value: 'trust',
                        },
                        {
                          label: 'Professional association',
                          value: 'professional-association',
                        },
                        {
                          label: 'PIC / PIV',
                          value: 'pic-piv',
                        },
                        {
                          label: 'SPV',
                          value: 'spv',
                        },
                        {
                          label: 'Other (please specify)',
                          value: 'other',
                        },
                      ]}
                      fullWidth
                      variant="muted"
                    />
                  )}
                />
              </FormElement>

              <OtherContainerSmall isClose={watch('legalEntityType') !== 'other'}>
                <FormElement>
                  <Controller
                    name="legalEntityTypeOther"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: watch('legalEntityType') === 'other',
                    }}
                    render={({ field, fieldState: { error, isTouched } }) => (
                      <Input placeholder="Other" hasError={isTouched && !!error} {...field} />
                    )}
                  />
                </FormElement>
              </OtherContainerSmall>
            </div>

            <FormElement>
              <FormLabel>State of Incorporation</FormLabel>
              <FormElementHint>
                This is the state in which your company is registered with the secretary of state or similar authority.
              </FormElementHint>
              <Controller
                name="stateOfIncorporation"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error, isTouched } }) => (
                  <Input placeholder="State of Incorporation" hasError={isTouched && !!error} {...field} />
                )}
              />
            </FormElement>

            <FormElement>
              <FormLabel>Year of Incorporation</FormLabel>
              <Controller
                name="yearOfIncorporation"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                  validate: validate4Digits,
                }}
                render={({ field, fieldState: { error, isTouched } }) => (
                  <Input placeholder="0000" maxLength={4} hasError={isTouched && !!error} {...field} />
                )}
              />
            </FormElement>

            <FormElement>
              <FormLabel>Employer Identification Number (EIN) or Tax ID</FormLabel>
              <Controller
                name="einOrTaxId"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error, isTouched } }) => (
                  <PasswordInput
                    placeholder="00-0000000"
                    mask="##-#######"
                    hasError={isTouched && !!error}
                    {...field}
                  />
                )}
              />
            </FormElement>

            <div>
              <NotificationList display="source-of-funds" />

              <FormElement>
                <FormLabel>Source of funds?</FormLabel>
                <div>
                  <OptionList>
                    {['PE/VC/Investor Funded', 'Revenue', 'Operations Generation', 'Loan', 'Other'].map(
                      (optionKey: string) => {
                        return (
                          <Option
                            isActive={sourceOfFunds.includes(optionKey)}
                            isSimple
                            key={optionKey}
                            onClick={() => handleSourceOfFundsChange(optionKey)}
                          >
                            <Checkbox
                              isChecked={sourceOfFunds.includes(optionKey)}
                              onCheckedChange={() => handleSourceOfFundsChange(optionKey)}
                              label={optionKey}
                            />
                          </Option>
                        );
                      }
                    )}
                  </OptionList>

                  <OtherContainerSmall isClose={!sourceOfFunds.includes('Other')}>
                    <StyledTextarea
                      placeholder="Please specify"
                      onChange={(value) => {
                        setSourceOfFundsOther(value);
                        setSourceOfFundsOtherError(value.length < 1);
                      }}
                      value={sourceOfFundsOther}
                      hasError={sourceOfFundsOtherError}
                    />
                  </OtherContainerSmall>
                </div>
              </FormElement>
            </div>
          </FormFields>

          <Line />

          <RegisterContent>
            <RegisterContentGrid>
              <Headline size="middle">
                EIN/Tax ID verification <small>(recommended)</small>
              </Headline>
              <Paragraph size="small">
                Please upload a document which verifies your EIN/Tax ID. We don’t require it now, but may ask for it
                later if we can’t automatically verify the information you provided.
              </Paragraph>
            </RegisterContentGrid>

            <FormFields>
              <FormElement>
                <FormLabel>Document Type</FormLabel>
                <Controller
                  name="einOrTaxIdDocumentType"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Dropdown
                      onChange={field.onChange}
                      active={field.value}
                      hasError={!!error}
                      placeholder="Select type..."
                      options={documentsDropdownOptions.filter((option) =>
                        ['w9', 'irs_letter', 'other'].includes(option.value)
                      )}
                      fullWidth
                      variant="muted"
                    />
                  )}
                />
              </FormElement>

              <FormElement>
                <FormLabel>Upload Document</FormLabel>
                <FileUpload
                  description="EIN/Tax ID verification"
                  type={watch('einOrTaxIdDocumentType')}
                  fileId={watch('einOrTaxIdDocumentId')}
                  onFileUploaded={handleFileUpload}
                  isFileRemovable={!rootEntity}
                  onFileRemove={handleFileRemove}
                />
              </FormElement>
            </FormFields>
          </RegisterContent>
        </RegisterContent>
        <RegisterAction>
          <Button variant="muted" onClick={() => navigate(ROUTE.REGISTER_OPEN_BANK_ACCOUNT)} type="button">
            Back
          </Button>
          <Button isLoading={isLoading} isDisabled={false}>
            Continue
          </Button>
        </RegisterAction>
      </Form>
    </RegisterContent>
  );
};
